import React, { use, useCallback, useState } from 'react';
import { ToggleButton } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faBan } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import DataTable from '../../DataTable/datatable.component';
import DateColumn from '../../DataTable/datatable.datecolumn';
import { getTableFilters, getSortOrder } from '../../Utilities/SessionStorage';
import SelectFilter from '../../DataTable/datatable.selectfilter';
import RecipeDialog from '../../Dialogs/RecipeDialog';
import { useLotStatuses } from '../../Utilities/Hooks';
import LotCountContext from '../../Contexts/LotCountContext';
import BulkReprocessButton from './BulkReprocessButton';
import BulkDeleteButton from './BulkDeleteButton';
import CreateNewLotButton from './CreateNewLotButton';
import { useNumberUtils } from '../../Utilities/useNumberUtils';
import { useUserAssignedBusinessUnits } from '../../Utilities/useUserAssignedBusinessUnits';

function LotListTable({ lots, tableFilters, tableName, autoResetPage, refreshLotList }) {

    const { t } = useTranslation();
    const { lotList, setLotList } = use(LotCountContext);
    const [showRecipeDialog, setShowRecipeDialog] = useState(false);
    const [selectedRecipe, setSelectedRecipe] = useState();
    const { lotStatuses } = useLotStatuses(tableFilters.includeGraded);

    const [showBulkActions, setShowBulkActions] = useState(false);
    const [selectAllChecked, setSelectAllChecked] = useState(false);
    const [selectedLots, setSelectedLots] = useState([]);

    const { formatLongNumber } = useNumberUtils();
    const { userAssignedBusinessUnits } = useUserAssignedBusinessUnits();

    const onToggleSelectedLot = (lotId) => {
        if (!selectedLots.includes(lotId)) {
            setSelectedLots(prev => [...prev, lotId]);
        } else {
            setSelectedLots(prev => prev.filter(x => x !== lotId));
        }
    };

    const onToggleSelectAll = (selectAllChecked, rows) => {
        if (selectAllChecked) {
            setSelectedLots(prev => prev.concat(rows.map(x => x.original.lotId)));
        } else {
            setSelectedLots([]);
        }
    };

    const columns = [
        {
            id: 'bulkActions',
            header: '',
            Header: ({ filteredRows }) => (
                <input
                    type="checkbox"
                    checked={selectAllChecked}
                    onChange={() => {
                        setSelectAllChecked(!selectAllChecked);
                        onToggleSelectAll(!selectAllChecked, filteredRows);
                    }}
                />
            ),
            Cell: ({ row }) => (
                <input
                    type="checkbox"
                    checked={selectedLots.includes(row.original.lotId)}
                    onChange={() => onToggleSelectedLot(row.original.lotId)
                    }
                />
            ),
            show: showBulkActions
        },
        {
            Header: t('LotList.ColHead.BusinessUnit'),
            accessor: 'businessUnit',
            Filter: SelectFilter,
            filter: 'equals'
        },
        {
            Header: t('LotList.ColHead.LotNumber'),
            accessor: 'lotNumber',
            Cell: (props) => {
                return (
                    <>
                        <Link to={{ pathname: `/lotdetails/${props.value}` }}>{props.value}</Link>
                        {props.row.original.isLocked && <FontAwesomeIcon icon={faLock} title={t('LotStatus.Locked')} className="ms-1" />}
                        {props.row.original.isBlocked && <FontAwesomeIcon icon={faBan} title={t('LotStatus.Blocked')} className="ms-1" />}
                    </>
                );
            },
            filter: 'fuzzyText'
        },
        {
            Header: t('LotList.ColHead.Status'),
            accessor: 'lotStatus',
            Filter: SelectFilter,
            filterTranslationPrefix: "LotStatus",
            filter: 'equals',
            options: lotStatuses,
            Cell: (props) => {
                return t('LotStatus.' + props.value);
            }
        },
        {
            Header: t('LotList.ColHead.Grade'),
            accessor: 'grade',
            filter: 'fuzzyText',
            Cell: (props) => {
                return (
                    <div className="clickable" onClick={() => {
                        setSelectedRecipe({ grade: props.value, businessUnitCode: props.row.original.businessUnit });
                        setShowRecipeDialog(true);
                    }}>
                        {props.value}
                    </div>
                );
            },
        },
        {
            Header: t('LotList.ColHead.PackoutGrade'),
            accessor: 'packoutGrade',
            filter: 'fuzzyText',
            Cell: (props) => {
                return (
                    <div className="clickable" onClick={() => {
                        setSelectedRecipe({ grade: props.value, businessUnitCode: props.row.original.businessUnit });
                        setShowRecipeDialog(true);
                    }}>
                        {props.value}
                    </div>
                );
            },
        },
        {
            Header: t('LotList.ColHead.Weight'),
            accessor: 'lotWeight',
            filter: 'NoFilter',
            Cell: ({ value, row }) => {
                return formatLongNumber(value) + (row.original.weightUnit == null ? '' : " " + row.original.weightUnit);
            }
        },
        ...userAssignedBusinessUnits.some(b => b.businessUnitType === "Package")
            ? [{
                Header: t('LotList.ColHead.PackageCount'),
                accessor: 'data.packageCount',
                filter: 'NoFilter'
            },
            {
                Header: t('LotList.ColHead.RedDotCodes'),
                accessor: 'data.redDotCodes',
                filter: 'NoFilter',
                Cell: ({ value, row }) => <span>{value?.sort().join(', ')}</span>
            }]
            : [],
        DateColumn({ header: t('LotList.ColHead.BuildStart'), accessor: 'buildStartUtc', width: "10%" }),
        DateColumn({ header: t('LotList.ColHead.BuildEnd'), accessor: 'buildEndUtc', width: "10%" }),
        {
            Header: t('LotList.ColHead.HasQIRs'),
            accessor: (row) => row.data.qirCount > 0 ? t('Application.Common.Yes') : t('Application.Common.No'),
            Filter: SelectFilter,
        },
        {
            Header: t('LotList.ColHead.LastModifiedBy'),
            accessor: 'lastModifiedBy',
            filter: 'fuzzyText'
        },
        {
            Header: t('LotList.ColHead.LastComment'),
            accessor: 'lastGradingComment',
            filter: 'fuzzyText'
        }
    ];

    const initialState = {
        pageSize: 20,
        sortBy: [
            {
                id: 'buildStartUtc',
                desc: true
            }
        ],
        hiddenColumns: ['bulkActions']
    };

    const tableId = tableName ?? "lots-table";
    const sortBy = getSortOrder(tableId);
    const filters = getTableFilters(tableId);

    if (sortBy) {
        initialState.sortBy = sortBy;
    }

    if (filters) {
        initialState.filters = filters;
    }

    let noLotsMessage;
    if (tableFilters.status) {
        noLotsMessage = <div>No {!tableFilters.includeGraded ? "ungraded" : ""} lots with status <strong>{t('LotStatus.' + tableFilters.status)}</strong> to show</div>;
    } else {
        noLotsMessage = <div>No {!tableFilters.includeGraded ? "ungraded" : ""} lots to show</div>;
    }

    const setFilteredLotList = useCallback((rows) => {
        const lotNumbers = rows.map(r => r.original.lotNumber);
        if (lotList.length !== lotNumbers.length || !lotNumbers.every((x, index) => x === lotList[index])) {
            setLotList(lotNumbers);
        }
    }, [lotList, setLotList]);

    return lots.length > 0
        ? <>
            <DataTable id={tableId} tableColumns={columns} tableData={lots} initialState={initialState} autoResetPage={autoResetPage.current} setFilteredRows={setFilteredLotList}>
                <div className="d-flex gap-1">
                    <ToggleButton
                        id="showBulkActions"
                        type="checkbox"
                        variant="outline-primary"
                        checked={showBulkActions}
                        value={showBulkActions}
                        onChange={() => setShowBulkActions(!showBulkActions)}
                    >
                        {showBulkActions
                            ? t("LotList.HideBulkActions")
                            : t("LotList.ShowBulkActions")
                        }
                    </ToggleButton>
                    <BulkReprocessButton
                        hidden={!showBulkActions || selectedLots.length === 0}
                        lotIds={selectedLots}
                        setSelectedLots={setSelectedLots}
                        refreshLotList={refreshLotList} />
                    <BulkDeleteButton
                        hidden={!showBulkActions || selectedLots.length === 0}
                        lotIds={selectedLots}
                        setSelectedLots={setSelectedLots}
                        refreshLotList={refreshLotList} />
                    <CreateNewLotButton />
                </div>
            </DataTable>
            {selectedRecipe &&
                <RecipeDialog showDialog={showRecipeDialog} closeDialog={() => setShowRecipeDialog(false)} name={selectedRecipe.grade} businessUnitCode={selectedRecipe.businessUnitCode} />
            }
        </>
        :
        <div className="d-flex flex-column mt-5">
            <h4 className="text-center">{noLotsMessage}</h4>
            <p className="text-center text-muted"><small>{t('LotList.CheckFiltersMessage')}</small></p>
        </div>;
}

export default LotListTable;